import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isFunction, noop } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useSelector, useDefaultId } from 'app/lib/hooks';
import { Button, ButtonSize } from 'app/components/core/Button';
import { Submit } from 'app/components/core/Form';
import { selectSetEnvironmentError } from 'app/store/jobs';
import {
    DialogWithChapters,
    DialogContext,
    DialogSummary,
    DialogPanel,
    DialogActions,
    DialogSidebar,
    DialogFormContent
} from 'app/components/features/DialogWithChapters';
import { ConfigureServiceAccountPanel } from 'app/components/features/JobDialog/ConfigureServiceAccountPanel.jsx';
import { CreateEnvironmentForm } from './CreateEnvironmentForm.jsx';

export function CreateEnvironmentDialog({ environment = null, onClose = noop }) {
    const { t } = useTranslation();
    const error = useSelector(selectSetEnvironmentError);
    const accId = useDefaultId('panel-acc');
    const envId = useDefaultId('panel-env');

    const [accSummary, setAccSummary] = useState(null);
    const [envSummary, setEnvSummary] = useState(null);

    const [accDetails, setAccDetails] = useState(null);

    function clearAccountDetails() {
        setAccSummary(null);
        setAccDetails(null);
    }

    const context = useMemo(() => {
        function handleAccountUpdated({ uid }) {
            const { onCreate } = accDetails;
            if (isFunction(onCreate)) {
                onCreate(uid);
            }
            clearAccountDetails();
        }

        return {
            onEditAccount: setAccDetails,
            onUpdateAccount: handleAccountUpdated,
            onUpdateAccountSummary: setAccSummary,
            onUpdateEnvironmentSummary: setEnvSummary
        };
    }, [accDetails]);

    return (
        <DialogWithChapters
            title={t('jobs:dialogs.environment.title', {
                context: environment ? 'edit' : 'create'
            })}
            error={error}
            id="create-env-dialog"
            height={480}
            onClose={onClose}>
            <DialogContext.Provider value={context}>
                <DialogFormContent>
                    {accDetails && (
                        <ConfigureServiceAccountPanel
                            id={accId}
                            account={accDetails.account}
                            onCancel={clearAccountDetails}
                        />
                    )}
                    <DialogPanel id={envId} label={t('jobs:dialogs.environment.panelTitle')} open>
                        <CreateEnvironmentForm environment={environment} onSuccess={onClose}>
                            <DialogActions>
                                <Button onClick={onClose} size={ButtonSize.MEDIUM}>
                                    {t('common:cancel')}
                                </Button>
                                <Submit size={ButtonSize.MEDIUM}>{t('common:apply')}</Submit>
                            </DialogActions>
                        </CreateEnvironmentForm>
                    </DialogPanel>
                </DialogFormContent>
                <DialogSidebar>
                    {accSummary && <DialogSummary formId={accId} summary={accSummary} />}
                    <DialogSummary formId={envId} summary={envSummary} />
                </DialogSidebar>
            </DialogContext.Provider>
        </DialogWithChapters>
    );
}

CreateEnvironmentDialog.propTypes = {
    environment: PropTypes.shape({
        uid: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        username: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired,
        warehouse: PropTypes.string.isRequired,
        snowflakeServiceAccountUid: PropTypes.string,
        slackChannelId: PropTypes.string
    }),
    onClose: PropTypes.func
};
